/**
 * Правила севооборота варианта посевного плана (AK-767, решение founder Р34).
 *
 * Чем это отличается от подбора в `useRotationMatch`
 * --------------------------------------------------
 * Подбор — разовая операция: кольцо живёт в `useState`, применил и забыл.
 * Правило ЖИВЁТ на сервере: у него есть область применения, приоритет, автор
 * и вариант плана. Именно поэтому два варианта плана становится чем отличать
 * — вариант Б отличается от варианта А набором правил, а не пересказом в
 * голове у плановика.
 *
 * Чтение и запись разделены намеренно
 * -----------------------------------
 * `usePlanRulesPreview` показывает, что случится, и НИЧЕГО не пишет.
 * `useApplyRotationRules` пишет — и только то, что показал предпросмотр.
 * Правило трогает тысячи полей разом, и «посмотрел — согласился» здесь
 * единственный безопасный порядок.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cropStructureKeys, planVariantKeys, techCardKeys } from '@/lib/planning/queryKeys';
import { techCardErrorText } from '@/lib/planning/techCards';
import { toast } from '@/lib/toast';

export type RuleKind = 'short' | 'ring';
export type ScopeLevel = 'farm' | 'brigade' | 'fields';

/** Позиция кольца с уже разрешённым именем — экран рисует чипы, а не uuid. */
export interface RingPosition {
  key: string;
  name: string;
  is_fallow: boolean;
}

export interface RotationRule {
  id: string;
  scenario_id: string;
  kind: RuleKind;
  after_crop_id: string | null;
  after_crop_name: string | null;
  then_crop_id: string | null;
  then_crop_name: string | null;
  then_is_fallow: boolean;
  ring: RingPosition[];
  scope_level: ScopeLevel;
  scope_brigade_id: string | null;
  scope_brigade_name: string | null;
  scope_field_ids: string[];
  position: number;
  is_active: boolean;
  created_by_user_id: string | null;
  /** Автор словами: правило переживает своего заводчика. */
  created_by_name: string | null;
}

export interface RuleAssignment {
  field_id: string;
  field_name: string;
  area_ha: number;
  crop_id: string;
  crop_name: string | null;
  rule_id: string;
}

/**
 * Виды конфликтов. Приходят кодом, а подпись живёт рядом с ним же
 * (`CONFLICT_TITLE`): группировать журнал по человеческому тексту значило бы
 * рассыпать группы при первой же правке формулировки.
 */
export type ConflictCode =
  | 'no_predecessor'
  | 'rules_disagree'
  | 'norm_violation'
  | 'fallow_suggested'
  | 'manual_override';

export interface RuleConflict {
  code: ConflictCode;
  field_id: string;
  field_name: string;
  rule_id: string | null;
  /** Текст с сервера: причина известна только там, где считалась раскладка. */
  message: string;
}

export interface RuleStats {
  rule_id: string;
  matched_fields: number;
  matched_area_ha: number;
  conflicts: number;
}

export interface RulesPreview {
  scenario_id: string;
  season_year: number;
  assignments: RuleAssignment[];
  conflicts: RuleConflict[];
  stats: RuleStats[];
  free_fields: number;
}

export interface RuleDraft {
  kind: RuleKind;
  after_crop_id?: string | null;
  then_crop_id?: string | null;
  then_is_fallow?: boolean;
  ring?: string[];
  scope_level: ScopeLevel;
  scope_brigade_id?: string | null;
  scope_field_ids?: string[];
  position?: number;
}

export const rotationRuleKeys = {
  all: () => ['planning', 'rotation-rules'] as const,
  list: (scenarioId: string | null) =>
    ['planning', 'rotation-rules', 'list', scenarioId ?? 'none'] as const,
  preview: (scenarioId: string | null) =>
    ['planning', 'rotation-rules', 'preview', scenarioId ?? 'none'] as const,
};

const base = (scenarioId: string) => `/api/v1/planning/scenarios/${scenarioId}/rotation-rules`;

/** Правила варианта по порядку применения (сверху вниз = по приоритету). */
export function usePlanRules(scenarioId: string | null) {
  return useQuery<RotationRule[], Error>({
    queryKey: rotationRuleKeys.list(scenarioId),
    queryFn: () => api.get<RotationRule[]>(base(scenarioId as string)),
    enabled: !!scenarioId,
    staleTime: 15_000,
  });
}

/**
 * Что правила предложат и где споткнутся. Ничего не пишет.
 *
 * Эта же ручка — журнал конфликтов: отдельного сохранённого журнала нет
 * намеренно, он устаревал бы в ту же секунду, когда агроном поправил правило
 * или занял поле рукой.
 */
export function usePlanRulesPreview(scenarioId: string | null, enabled = true) {
  return useQuery<RulesPreview, Error>({
    queryKey: rotationRuleKeys.preview(scenarioId),
    queryFn: () => api.get<RulesPreview>(`${base(scenarioId as string)}/preview`),
    enabled: !!scenarioId && enabled,
    staleTime: 15_000,
  });
}

/**
 * Правки правил трогают и сами правила, и предпросмотр: он считается ПО ним,
 * и оставить его прежним значило бы показать раскладку по вчерашнему набору.
 */
function useInvalidateRules(scenarioId: string | null) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: rotationRuleKeys.list(scenarioId) });
    void qc.invalidateQueries({ queryKey: rotationRuleKeys.preview(scenarioId) });
  };
}

export function useCreateRotationRule(scenarioId: string | null) {
  const invalidate = useInvalidateRules(scenarioId);
  return useMutation<RotationRule, Error, RuleDraft>({
    mutationFn: (draft) => api.post<RotationRule>(base(scenarioId as string), draft),
    onSuccess: () => {
      toast.success('Правило добавлено. Нажмите «Применить», чтобы разнести культуры.');
    },
    onError: (err) => toast.error(techCardErrorText(err)),
    onSettled: invalidate,
  });
}

export function useDeleteRotationRule(scenarioId: string | null) {
  const invalidate = useInvalidateRules(scenarioId);
  return useMutation<void, Error, string>({
    mutationFn: (ruleId) => api.delete<void>(`${base(scenarioId as string)}/${ruleId}`),
    onSuccess: () => {
      // Прямо говорим, что назначенные культуры остались: иначе агроном ждёт,
      // что вместе с правилом с полей пропадут и посевы, и пойдёт искать их.
      toast.success('Правило убрано. Назначенные им культуры остались в плане.');
    },
    onError: (err) => toast.error(techCardErrorText(err)),
    onSettled: invalidate,
  });
}

export interface ApplyResult {
  applied: number;
  area_ha: number;
  conflicts: RuleConflict[];
}

/** Записать то, что показал предпросмотр. */
export function useApplyRotationRules(scenarioId: string | null) {
  const qc = useQueryClient();
  return useMutation<ApplyResult, Error, { ruleIds?: string[] }>({
    mutationFn: ({ ruleIds }) =>
      api.post<ApplyResult>(`${base(scenarioId as string)}/apply`, {
        rule_ids: ruleIds ?? [],
      }),
    onSuccess: (res) => {
      toast.success(applySummary(res));
    },
    onError: (err) => toast.error(techCardErrorText(err)),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: rotationRuleKeys.all() });
      // Применение пишет ПОСЕВЫ — вместе с ними меняются строки экрана,
      // покрытие техкарт и срез СПП. Не обновить их значило бы оставить
      // агронома с таблицей, которая спорит с только что нажатой кнопкой.
      void qc.invalidateQueries({ queryKey: planVariantKeys.all() });
      void qc.invalidateQueries({ queryKey: techCardKeys.all() });
      void qc.invalidateQueries({ queryKey: cropStructureKeys.all() });
      void qc.invalidateQueries({ queryKey: ['planning', 'plan-variant', 'fields'] });
    },
  });
}

/**
 * Итог применения словами.
 *
 * Про конфликты говорим ВСЕГДА, когда они есть: «применено на 1 964 полях» без
 * второй половины читается как «готово», хотя часть полей осталась
 * неразобранной — и агроном узнал бы об этом, только пересчитав таблицу.
 */
export function applySummary(res: ApplyResult): string {
  const parts: string[] = [];
  parts.push(res.applied === 0 ? 'Правила ничего не изменили' : `Культура назначена на ${res.applied} ${fieldsWord(res.applied)}`);
  if (res.applied > 0 && res.area_ha > 0) parts.push(`${formatHaShort(res.area_ha)} га`);
  if (res.conflicts.length > 0) {
    parts.push(`конфликтов: ${res.conflicts.length} — разберите вручную`);
  }
  return parts.join(' · ');
}

/** «1 поле» / «2 поля» / «5 полей» — агроном читает текст, а не число. */
export function fieldsWord(count: number): string {
  const n = Math.abs(count) % 100;
  const last = n % 10;
  if (n > 10 && n < 20) return 'полях';
  if (last === 1) return 'поле';
  return 'полях';
}

function formatHaShort(ha: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(ha);
}

/** Заголовок группы в журнале конфликтов — по коду, а не по тексту причины. */
export const CONFLICT_TITLE: Record<ConflictCode, string> = {
  no_predecessor: 'Предшественник неизвестен',
  rules_disagree: 'Правила спорят между собой',
  norm_violation: 'Культура вернётся слишком рано',
  fallow_suggested: 'Правило ведёт к пару',
  manual_override: 'Назначено руками поверх правила',
};

/**
 * Насколько конфликт срочный.
 *
 * `blocking` — правило вовсе не смогло решить (поле останется пустым);
 * `warning` — решение есть, но человек уже сказал своё слово или система
 * предупреждает о риске. Красным красим только первое: покрасить всё значило
 * бы не покрасить ничего.
 */
export function conflictSeverity(code: ConflictCode): 'blocking' | 'warning' {
  return code === 'no_predecessor' || code === 'rules_disagree' ? 'blocking' : 'warning';
}
